import { useState } from 'react';
import { CheckCircle2, Edit2 } from 'lucide-react';
import { createTenantLocation } from '@/lib/api';
import { PlaceAutocomplete, type ResolvedAddress } from './PlaceAutocomplete';
import styles from './Form.module.css';

/**
 * Tenant-scoped fork of AddLocationForm.
 *
 * Same fields, validation, and address-autocomplete behaviour as
 * AddLocationForm, but it takes an explicit `tenantId` and submits via
 * createTenantLocation(tenantId, body) so a super_admin can add a location
 * to any tenant from that tenant's detail page. AddLocationForm posts to
 * the caller-scoped POST /locations and so can only ever create under the
 * caller's own tenant; this one targets the tenant in the path.
 *
 * Forked rather than parameterised so AddLocationForm and LocationsPage
 * stay byte-unchanged, the same way the SP dashboard forked LocationInfoCard.
 * There is no tenant picker: the tenant is fixed by the page.
 */

interface TenantAddLocationFormProps {
  tenantId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'multifamily', label: 'Multi-Family' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'public', label: 'Public' },
  { value: 'fleet', label: 'Fleet' },
];

const HAS_GOOGLE = Boolean(import.meta.env['VITE_GOOGLE_MAPS_KEY']);

export function TenantAddLocationForm({ tenantId, onSuccess, onCancel }: TenantAddLocationFormProps) {
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('US');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [type, setType] = useState('commercial');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Whether the user wants to manually edit address fields (toggle off the autocomplete view)
  const [manualMode, setManualMode] = useState(!HAS_GOOGLE);
  const [autocompleteResolved, setAutocompleteResolved] = useState(false);

  const onPlaceSelected = (a: ResolvedAddress) => {
    setStreet(a.street);
    setCity(a.city);
    setState(a.state);
    setZip(a.zip);
    setCountry(a.country);
    setLat(String(a.lat));
    setLng(String(a.lng));
    setAutocompleteResolved(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const coordinates =
        lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : undefined;

      await createTenantLocation(tenantId, {
        name: name.trim(),
        type,
        address: {
          street: street.trim(),
          city: city.trim(),
          state: state.trim().toUpperCase(),
          zip: zip.trim(),
          country: country.trim().toUpperCase(),
          coordinates,
        },
      });

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create location');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {error && <div className={styles.formError}>{error}</div>}

      <div className={styles.field}>
        <label className={`${styles.label} ${styles.required}`}>Location Name</label>
        <input
          className={styles.input}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. EVII Mission Hills"
          required
          maxLength={200}
        />
      </div>

      <div className={styles.field}>
        <label className={`${styles.label} ${styles.required}`}>Type</label>
        <select className={styles.select} value={type} onChange={(e) => setType(e.target.value)} required>
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {/* Address: Google Places Autocomplete (when key configured) or manual fields */}
      {HAS_GOOGLE && !manualMode ? (
        <div className={styles.field}>
          <label className={`${styles.label} ${styles.required}`}>Address</label>
          <PlaceAutocomplete onSelect={onPlaceSelected} placeholder="Start typing an address..." />
          {autocompleteResolved && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8,
              padding: '10px 12px', background: 'var(--color-brand-bg)', border: '1px solid var(--color-brand-brd)',
              borderRadius: 8, marginTop: 8,
            }}>
              <div style={{ fontSize: 13, color: 'var(--color-tx)', lineHeight: 1.4 }}>
                <CheckCircle2 size={14} style={{ verticalAlign: 'middle', color: 'var(--color-brand)', marginRight: 4 }} />
                <strong>{street}</strong>
                <br />
                <span style={{ color: 'var(--color-tx-m)' }}>{city}, {state} {zip} · {country}</span>
                <br />
                <span style={{ color: 'var(--color-tx-m)', fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>
                  {parseFloat(lat).toFixed(6)}, {parseFloat(lng).toFixed(6)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setManualMode(true)}
                style={{
                  background: 'transparent', border: 'none', color: 'var(--color-tx-m)',
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                }}
                title="Edit fields manually"
              >
                <Edit2 size={12} /> Edit
              </button>
            </div>
          )}
          <p className={styles.hint}>
            Powered by Google Places. <button type="button" onClick={() => setManualMode(true)} style={{ background: 'none', border: 'none', color: 'var(--color-brand)', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>Enter manually</button> if you prefer.
          </p>
        </div>
      ) : (
        <>
          {!HAS_GOOGLE && (
            <p className={styles.hint} style={{ margin: 0 }}>
              💡 Set <code style={{ background: 'var(--color-bg2)', padding: '1px 4px', borderRadius: 3 }}>VITE_GOOGLE_MAPS_KEY</code> in <code>apps/admin/.env</code> to enable address autocomplete.
            </p>
          )}
          {manualMode && HAS_GOOGLE && (
            <button
              type="button"
              onClick={() => { setManualMode(false); setAutocompleteResolved(false); }}
              style={{ background: 'none', border: 'none', color: 'var(--color-brand)', cursor: 'pointer', fontSize: 13, alignSelf: 'flex-start', padding: 0 }}
            >
              ← Back to address search
            </button>
          )}

          <div className={styles.field}>
            <label className={`${styles.label} ${styles.required}`}>Street</label>
            <input
              className={styles.input}
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="123 Main St"
              required
            />
          </div>

          <div className={styles.row3}>
            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>City</label>
              <input className={styles.input} type="text" value={city} onChange={(e) => setCity(e.target.value)} required />
            </div>
            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>State</label>
              <input className={styles.input} type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder="CA" minLength={2} maxLength={3} required />
            </div>
            <div className={styles.field}>
              <label className={`${styles.label} ${styles.required}`}>ZIP</label>
              <input className={styles.input} type="text" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="92110" required />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Country</label>
            <input className={styles.input} type="text" value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} />
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Latitude</label>
              <input className={styles.input} type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="32.7423" />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Longitude</label>
              <input className={styles.input} type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-117.1954" />
            </div>
          </div>
          <p className={styles.hint} style={{ margin: 0 }}>
            Leave coordinates blank to auto-geocode on the server (if configured).
          </p>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="submit"
          className={styles.btnPrimary}
          disabled={submitting || !name || !street || !city || !state || !zip}
        >
          {submitting ? 'Creating...' : 'Create Location'}
        </button>
      </div>
    </form>
  );
}
